import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Users as UsersIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'Intake Volunteer'
  });

  useEffect(() => {
    if (user?.role !== 'Admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/register`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User added successfully');
      setShowAddUser(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'Intake Volunteer' });
      fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#0072BC] border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      {/* Header */}
      <header className="backdrop-blur-md bg-slate-900/50 border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <UsersIcon className="w-6 h-6 text-[#0072BC]" />
                  User Management
                </h1>
                <p className="text-sm text-slate-400">Manage volunteer and staff accounts</p>
              </div>
            </div>

            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
              <DialogTrigger asChild>
                <Button className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="add-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-user-dialog" className="bg-slate-900 border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white">Add New User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
                      required
                      data-testid="new-user-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
                      required
                      data-testid="new-user-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
                      required
                      minLength="8"
                      data-testid="new-user-password-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Role</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none appearance-none"
                      required
                      data-testid="new-user-role-select"
                    >
                      <option value="Intake Volunteer" className="bg-slate-800">Intake Volunteer</option>
                      <option value="Case Lead" className="bg-slate-800">Case Lead</option>
                      <option value="Parish Staff" className="bg-slate-800">Parish Staff</option>
                      <option value="Admin" className="bg-slate-800">Admin</option>
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#0072BC] hover:bg-[#005a96]"
                    data-testid="submit-new-user-btn"
                  >
                    Add User
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="border-b border-white/10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {users.map((userItem) => (
                  <tr key={userItem.id} className="hover:bg-white/5 transition-colors" data-testid={`user-row-${userItem.id}`}>
                    <td className="px-6 py-4 text-sm font-medium text-white">{userItem.full_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{userItem.email}</td>
                    <td className="px-6 py-4">
                      <span className="badge badge-new">{userItem.role}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${
                        userItem.is_active ? 'badge-assisted' : 'badge-closed'
                      }`}>
                        {userItem.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {new Date(userItem.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
